/**
 * Per-PAGE agent-CRM + back-office desk guided tours (EPIC #1952 Wave 3, #1956).
 *
 * Where the agent SECTION tours (``agentSectionTours.ts``) walk a whole nav GROUP
 * from Training, these are focused, single-PAGE deep-dives wired to the route via
 * ``routeTours.ts`` so the header launcher (the GraduationCap button) starts them
 * on the page in focus. One short tour per genuinely COMPLEX page NOT already
 * covered by a section tour -- the agent's Farm / Buyers / Agenda / Messaging /
 * FICA matrix / Referrals / Conveyancers / Connect surfaces, the per-deal detail
 * file, and the two back-office desk pages (the File Cover Page and Behind
 * Schedule). Genuinely WYSIWYG pages (a flat list / single form) and the
 * record-detail editors get NO tour and are documented as exclusions in the PR;
 * the launcher is simply absent there (hidden-when-no-tour).
 *
 * Conventions (mirror adminPageTours.ts / agentSectionTours.ts):
 *  - Copy (D4-ONB-011 / D4-WLB): agency-neutral via the ``{agency}`` token, plain
 *    professional language for the field agent, NO emojis, never a hardcoded
 *    agency/vendor/location name (the D4-WLB-008 grep guard stays green).
 *  - Gating (D4-ONB-004): a money step gates on ``permFlag: "view_commission"``
 *    (+ ``module: "financial"`` where money is shown); a module-driven surface
 *    gates on its module -- Farm/Referrals on ``farm``, Messaging on
 *    ``messaging``, the FICA matrix on ``fica`` -- exactly the CrmSidebar
 *    ``itemVisible`` rule, so a step silently disappears when its surface is off.
 *  - Audience (#1347): the agent-CRM page tours declare
 *    ``audience: { excludePreset: BACK_OFFICE_PRESETS }`` so a single-surface
 *    back-office persona is never offered them (a plain agent + an admin-preview,
 *    whose preset is null, keep them). The two back-office desk pages require the
 *    ``capture_deal`` flag, so those tours gate the whole tour on
 *    ``audience: { flag: "capture_deal" }`` -- NOT adminOnly (they are back-office,
 *    not admin).
 *  - page=spotlight / action=beacon (#1341/#1342): a page/section step stays the
 *    default ``page`` (spotlight); only a single button-press/modal step is
 *    ``kind: "action"`` (a beacon the user clicks). Every opener targets ``center``
 *    or an always-rendered ``page-*`` anchor so the tour starts standalone while
 *    the page is still loading (the engine skips a momentarily-absent target).
 *  - Targets are stable ``data-tour="..."`` anchors on REAL, visible elements.
 *
 * REQs: D4-ONB-008 (per-surface tours), D4-ONB-011 (agency-neutral copy),
 * D4-ONB-004 / D4-ONB-009 (gating).
 */
import type { TourDefinition } from "./types";
import { BACK_OFFICE_PRESETS } from "./agentSectionTours";

// ===========================================================================
// Agent CRM pages (#1956)
// ===========================================================================

/**
 * Farm (``/crm/farm``) -- the agent's territory: the streets and complexes they
 * work, with every owner record to hand. Read-only list (no create button), so
 * the tour is page + a search/territory step, no action step. Farm module-gated.
 * It also points at the two farm-adjacent LOOM surfaces in the sidebar right
 * below Farm -- Canvassing and Property lookup (same audience, same module).
 */
export const AGENT_FARM_PAGE_TOUR: TourDefinition = {
  id: "agent-farm-page",
  title: "Farm tour",
  audience: { excludePreset: BACK_OFFICE_PRESETS },
  steps: [
    {
      id: "welcome",
      route: "/crm/farm",
      target: "page-farm",
      section: "Welcome",
      module: "farm",
      title: "Your territory",
      body: "Farm is the area you work -- the streets and complexes you know better than anyone, with every owner record to hand. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "bottom",
    },
    {
      id: "search",
      route: "/crm/farm",
      target: "page-farm",
      section: "Find a record",
      module: "farm",
      title: "Find an owner fast",
      body: "Search by address, complex name, suburb, owner name, erf or transfer number, and narrow by area or status, so you can pull up any owner in seconds.",
      placement: "bottom",
    },
    {
      id: "canvassing",
      route: "/crm/farm",
      target: "nav-/crm/canvassing",
      section: "Canvassing",
      navGroup: "people",
      module: "farm",
      title: "Canvassing",
      body: "Canvassing prices the owner contact list for a street or area in your farm. LOOM quotes the cost before anything is spent. Bulk lists are ordered by the office; to reach one owner, use Property lookup.",
      placement: "right",
    },
    {
      id: "property-lookup",
      route: "/crm/farm",
      target: "nav-/crm/property-lookup",
      section: "Property lookup",
      navGroup: "people",
      module: "farm",
      title: "Property lookup",
      body: "Property lookup finds a property and shows its registered owner for free. A new deeds report or an owner's contact details shows its cost and asks you to confirm first; contact details come from your own monthly allowance.",
      placement: "right",
    },
    {
      id: "keep-current",
      route: "/crm/farm",
      target: "page-farm",
      section: "Stay current",
      module: "farm",
      title: "Keep your area current",
      body: "Open any record to see its full history. Keep your farm up to date and you will always know your area better than anyone. That is your Farm -- explore at your own pace.",
      placement: "center",
    },
  ],
};

/**
 * Buyers (``/crm/buyers``) -- the agent's buyer leads, matched to their stock.
 * Carries a New Buyer create button, so it earns an action step.
 */
export const AGENT_BUYERS_PAGE_TOUR: TourDefinition = {
  id: "agent-buyers-page",
  title: "Buyers tour",
  audience: { excludePreset: BACK_OFFICE_PRESETS },
  steps: [
    {
      id: "welcome",
      route: "/crm/buyers",
      target: "page-buyers",
      section: "Welcome",
      title: "Your buyers",
      body: "Buyers is where you track every buyer lead and match them to your stock, so the right property reaches the right person first. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "bottom",
    },
    {
      id: "matching",
      route: "/crm/buyers",
      target: "page-buyers",
      section: "Match to stock",
      title: "Match to your listings",
      body: "Move each buyer through their stages and match them to your listings. Search and filter to find the right buyer the moment new stock comes in.",
      placement: "center",
    },
    {
      // ACTION step (#1342): beacon on the New Buyer button -- the key call to
      // action. Clicking the beacon reveals the tooltip; it never clicks the
      // button itself, so nothing is captured.
      id: "new-buyer",
      route: "/crm/buyers",
      target: "buyers-new",
      kind: "action",
      section: "New buyer",
      title: "Add a buyer",
      body: "New Buyer adds a lead to your book -- their details and what they are looking for. Give the beacon a click to see where it sits. That is Buyers.",
      placement: "left",
    },
  ],
};

/**
 * Agenda (``/crm/appointments``) -- the agent's day: viewings, valuations,
 * meetings and calls, in day / week / month views.
 */
export const AGENT_APPOINTMENTS_PAGE_TOUR: TourDefinition = {
  id: "agent-appointments-page",
  title: "Agenda tour",
  audience: { excludePreset: BACK_OFFICE_PRESETS },
  steps: [
    {
      id: "welcome",
      route: "/crm/appointments",
      target: "page-appointments",
      section: "Welcome",
      title: "Your agenda",
      body: "Your Agenda lays out your viewings, valuations, meetings and calls so you can plan your week at a glance. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "bottom",
    },
    {
      id: "views",
      route: "/crm/appointments",
      target: "appointments-toolbar",
      section: "Views",
      title: "Day, week and month",
      body: "Switch between day, week and month views and step through the dates, so you can zoom in on today or plan the week ahead.",
      placement: "bottom",
    },
    {
      // ACTION step (#1342): beacon on the New Appointment button.
      id: "new-appointment",
      route: "/crm/appointments",
      target: "appointments-new",
      kind: "action",
      section: "New appointment",
      title: "Add an appointment",
      body: "New Appointment books a viewing, valuation, meeting or call onto your agenda. Give the beacon a click to see where it sits. That is your Agenda.",
      placement: "left",
    },
  ],
};

/**
 * Messaging (``/crm/messaging``) -- WhatsApp and email with contacts, kept as a
 * thread against each person. Messaging module-gated.
 */
export const AGENT_MESSAGING_PAGE_TOUR: TourDefinition = {
  id: "agent-messaging-page",
  title: "Messaging tour",
  audience: { excludePreset: BACK_OFFICE_PRESETS },
  steps: [
    {
      id: "welcome",
      route: "/crm/messaging",
      target: "page-messaging",
      section: "Welcome",
      module: "messaging",
      title: "Talking to your contacts",
      body: "Messaging is how you reach your contacts by WhatsApp and email, with the full thread kept against each person. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "bottom",
    },
    {
      id: "threads",
      route: "/crm/messaging",
      target: "page-messaging",
      section: "Threads",
      module: "messaging",
      title: "One thread per contact",
      body: "Every WhatsApp and email is kept as a thread against the person, so you always have the full conversation in front of you.",
      placement: "center",
    },
    {
      // ACTION step (#1342): beacon on the Compose button.
      id: "compose",
      route: "/crm/messaging",
      target: "messaging-compose",
      kind: "action",
      section: "Compose",
      module: "messaging",
      title: "Send a message",
      body: "Compose starts a new WhatsApp or email to a contact. Give the beacon a click to see where it sits -- every send is logged against the person automatically. That is Messaging.",
      placement: "left",
    },
  ],
};

/**
 * FICA Matrix (``/crm/fica-matrix``) -- the compliance documents still
 * outstanding on each deal, party by party. FICA module-gated.
 */
export const AGENT_FICA_MATRIX_PAGE_TOUR: TourDefinition = {
  id: "agent-fica-matrix-page",
  title: "FICA Matrix tour",
  audience: { excludePreset: BACK_OFFICE_PRESETS },
  steps: [
    {
      id: "welcome",
      route: "/crm/fica-matrix",
      target: "page-fica-matrix",
      section: "Welcome",
      module: "fica",
      title: "What is still outstanding",
      body: "The FICA Matrix flags the compliance documents still missing on each deal, party by party -- the paperwork that has to be done before a transfer can register. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "bottom",
    },
    {
      id: "summary",
      route: "/crm/fica-matrix",
      target: "fica-matrix-summary",
      section: "Outstanding",
      module: "fica",
      title: "Your outstanding count",
      body: "These tiles count the deals, parties and documents still flagged, so you can see at a glance how much is left to collect.",
      placement: "bottom",
    },
    {
      id: "clear",
      route: "/crm/fica-matrix",
      target: "page-fica-matrix",
      section: "Clear the flags",
      module: "fica",
      title: "Clear what is missing",
      body: "Each flagged deal shows the exact documents still needed, party by party. Open the deal and use its FICA tab to start a party's FICA yourself and collect what is missing -- a transfer cannot register until the flags are cleared. That is the FICA Matrix.",
      placement: "center",
    },
  ],
};

/**
 * Referrals (``/crm/referrals``) -- refer a property or buyer to a colleague,
 * and pick up referrals sent your way. Farm module-gated.
 */
export const AGENT_REFERRALS_PAGE_TOUR: TourDefinition = {
  id: "agent-referrals-page",
  title: "Referrals tour",
  audience: { excludePreset: BACK_OFFICE_PRESETS },
  steps: [
    {
      id: "welcome",
      route: "/crm/referrals",
      target: "page-referrals",
      section: "Welcome",
      module: "farm",
      title: "Referrals in flight",
      body: "Referrals is where you hand a property or a buyer to a colleague, and pick up the referrals sent your way. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "bottom",
    },
    {
      id: "due",
      route: "/crm/referrals",
      target: "page-referrals",
      section: "When it is due",
      module: "farm",
      title: "You are told when it pays",
      body: "Filter to the referrals where a commission is due, and you are notified the moment one becomes payable, so nothing owed to you is missed.",
      placement: "center",
    },
    {
      // ACTION step (#1342): beacon on the New Referral button.
      id: "new-referral",
      route: "/crm/referrals",
      target: "referrals-new",
      kind: "action",
      section: "New referral",
      module: "farm",
      title: "Make a referral",
      body: "New Referral records a property or buyer you are referring to a colleague. Give the beacon a click to see where it sits. That is Referrals.",
      placement: "left",
    },
  ],
};

/**
 * Conveyancers (``/crm/conveyancers``) -- the conveyancing-firm directory used
 * when assigning an attorney to a deal. The Add Conveyancer button is admin-only,
 * so the tour never anchors a required step on it.
 *
 * Audience (issue #1956 fix): ``/crm/conveyancers`` is guarded by
 * ``ConveyancerRoute``, which requires the ``capture_deal`` flag (admins + the
 * paralegal/finance/accounts/manager/finance_accounts/office_admin desks) and 403s
 * a plain agent. So the tour gates on ``audience: { flag: "capture_deal" }`` -- the
 * back-office desk flag (NOT ``excludePreset``, which would withhold it from
 * EXACTLY the capture_deal personas who can reach the page, leaving it offered to
 * nobody). It is classed as a back-office desk tour (``back-office-*`` id).
 */
export const BACK_OFFICE_CONVEYANCERS_PAGE_TOUR: TourDefinition = {
  id: "back-office-conveyancers-page",
  title: "Conveyancers tour",
  audience: { flag: "capture_deal" },
  steps: [
    {
      id: "welcome",
      route: "/crm/conveyancers",
      target: "center",
      section: "Welcome",
      title: "Your attorney directory",
      body: "Conveyancers is the directory of conveyancing firms -- the attorneys who handle transfers. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "directory",
      route: "/crm/conveyancers",
      target: "conveyancers-page",
      section: "Directory",
      title: "Every firm in one place",
      body: "Each firm shows its branches and contacts, so you always have the right attorney's details to hand.",
      placement: "bottom",
    },
    {
      id: "search",
      route: "/crm/conveyancers",
      target: "conveyancers-search",
      section: "Find a firm",
      title: "Find a firm",
      body: "Search the directory to find a firm and its contact details fast -- handy when you are assigning an attorney to a deal. That is Conveyancers.",
      placement: "bottom",
    },
  ],
};

/**
 * Connect (``/crm/connect``) -- link the agent's own AI (Claude, ChatGPT) to the
 * CRM so they can ask about their data in plain language. No money gate.
 */
export const AGENT_CONNECT_PAGE_TOUR: TourDefinition = {
  id: "agent-connect-page",
  title: "Connect your AI tour",
  audience: { excludePreset: BACK_OFFICE_PRESETS },
  steps: [
    {
      id: "welcome",
      route: "/crm/connect",
      target: "page-connect",
      section: "Welcome",
      title: "Connect your AI",
      body: "Connect links your own AI -- Claude or ChatGPT -- to your CRM, so you can ask about your contacts, listings and deals in plain language. It only ever sees your own data. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "bottom",
    },
    {
      id: "status",
      route: "/crm/connect",
      target: "connect-status",
      section: "Status",
      title: "Your connection status",
      body: "This badge shows whether your AI is connected. It turns green once your AI actually calls a tool for the first time.",
      placement: "bottom",
    },
    {
      id: "check",
      route: "/crm/connect",
      target: "connect-check",
      section: "Test it",
      title: "Test the link",
      body: "Once your AI has connected, use Check your connection to confirm the link is working. That is Connect -- explore at your own pace.",
      placement: "top",
    },
  ],
};

/**
 * Deal detail (``/crm/deals/:id``) -- the per-deal file the agent works from
 * offer to registration. Wired by a ``/crm/deals`` PREFIX entry in routeTours
 * (the exact ``/crm/deals`` list keeps the section tour; the prefix catches the
 * ``/:id`` detail AND ``/crm/deals/add-sale``), so every step -- including the
 * ``center`` opener -- uses the ``/crm/deals/:id`` PATTERN route. The launcher
 * only offers this tour ON a deal-detail page, so the engine's ``:param``-aware
 * route match (issue #1956) keeps it on the concrete ``/crm/deals/42`` rather
 * than bouncing the user to the list. The commission step gates on
 * ``view_commission`` so a value-free preset never sees a money step.
 */
export const AGENT_DEAL_DETAIL_PAGE_TOUR: TourDefinition = {
  id: "agent-deal-detail-page",
  title: "Deal file tour",
  audience: { excludePreset: BACK_OFFICE_PRESETS },
  steps: [
    {
      id: "welcome",
      route: "/crm/deals/:id",
      target: "center",
      section: "Welcome",
      title: "Your deal file",
      body: "This is the file for a single deal -- everything you need to take it from offer to registration in one place. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "tabs",
      route: "/crm/deals/:id",
      target: "deal-detail-tabs",
      section: "Tabs",
      title: "Work the file through the tabs",
      body: "These tabs take you through the file -- the deal's details, the buyers and sellers, the documents and the progress. Step through them to keep the deal moving.",
      placement: "bottom",
    },
    {
      // The FICA tab's content only mounts while that tab is active, so the
      // engine clicks the FICA tab button (openVia -- a pure tab switch, no data
      // change) and then spotlights the tab's content region.
      id: "fica",
      route: "/crm/deals/:id",
      target: "deal-fica-tab",
      openVia: "deal-tab-fica",
      section: "FICA",
      title: "The FICA tab",
      body: "The FICA tab, third along after Buyers / Sellers, is where each buyer's and seller's FICA is done. Start FICA on a party to open their case, run the checks, and collect the documents still needed -- you can do this yourself, without waiting for the office.",
      placement: "top",
    },
    {
      // Money step (#1342): gated on view_commission so a value-free preset never
      // sees it. A page step on the tabs anchor -- the Commission tab lives here.
      id: "commission",
      route: "/crm/deals/:id",
      target: "deal-detail-tabs",
      section: "Commission",
      permFlag: "view_commission",
      module: "financial",
      title: "The commission tab",
      body: "The Commission tab shows the split on this deal -- who shares it and in what proportion. You capture it here; it is then checked and signed off by someone else before anything is paid.",
      placement: "bottom",
    },
    {
      id: "progress",
      route: "/crm/deals/:id",
      target: "deal-detail-tabs",
      section: "Progress",
      title: "Where the deal stands",
      body: "The Progress tab shows where the deal is in the system -- the milestones reached, the document folders and the latest updates from the conveyancer. That is your deal file -- explore at your own pace.",
      placement: "bottom",
    },
  ],
};

// ===========================================================================
// Back-office pages (#1956)
// ===========================================================================

/**
 * File Cover Page (``/legal/:otpId``) -- the digitised cover sheet the paralegal
 * works a deal from: the milestone grid, the FICA checklist and the dated
 * conveyancer updates. Wired by a ``/legal`` PREFIX entry in routeTours, which
 * ALSO matches the bare ``/legal`` queue list -- so the opener is a ``center``
 * narrative that always renders; the cover-page anchor steps that follow simply
 * advance (TARGET_NOT_FOUND) on the queue, so the tour is never broken there.
 * The whole tour gates on ``capture_deal`` -- the back-office desk flag. The
 * cover page masks commission for value-free presets, so no money step is needed.
 */
export const BACK_OFFICE_FILE_COVER_PAGE_TOUR: TourDefinition = {
  id: "back-office-file-cover-page",
  title: "File Cover Page tour",
  audience: { flag: "capture_deal" },
  steps: [
    {
      id: "welcome",
      route: "/legal/:otpId",
      target: "center",
      section: "Welcome",
      title: "The digitised cover sheet",
      body: "The File Cover Page is the deal's cover sheet, digitised -- the milestones, the FICA checklist and the conveyancer's updates, all on one page. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "cover",
      route: "/legal/:otpId",
      target: "file-cover-page",
      section: "Overview",
      title: "Your file at a glance",
      body: "This page tracks the whole file -- the dates that matter, the documents collected and the running notes -- so you always know exactly where the deal stands.",
      placement: "bottom",
    },
    {
      id: "milestones",
      route: "/legal/:otpId",
      target: "cover-milestones",
      section: "Milestones",
      title: "Track each milestone",
      body: "The Milestones grid records each step of the transfer -- offer, FICA, bond grant, lodgement, registration. Click a dotted cell to enter its date; the automatic lines fill themselves.",
      placement: "top",
    },
    {
      id: "fica",
      route: "/legal/:otpId",
      target: "cover-fica",
      section: "FICA",
      title: "Tick documents as they arrive",
      body: "The FICA grid is the deal's compliance checklist. Tick each document off as it arrives, party by party, so nothing is missed before registration.",
      placement: "top",
    },
    {
      id: "updates",
      route: "/legal/:otpId",
      target: "cover-conveyancer-updates",
      section: "Updates",
      title: "Log the attorney's updates",
      body: "Record the conveyancer's dated updates here -- bond granted, transfer lodged, registered. The agent sees these on the deal's Progress tab. That is the File Cover Page.",
      placement: "top",
    },
  ],
};

/**
 * Behind Schedule (``/behind-schedule``) -- the deals whose FICA, deposit,
 * bond-grant or balance dates have slipped, for the paralegal and the weekly
 * management meeting. A capture_deal-gated back-office desk page.
 */
export const BACK_OFFICE_BEHIND_SCHEDULE_TOUR: TourDefinition = {
  id: "back-office-behind-schedule",
  title: "Behind Schedule tour",
  audience: { flag: "capture_deal" },
  steps: [
    {
      id: "welcome",
      route: "/behind-schedule",
      target: "center",
      section: "Welcome",
      title: "What has slipped",
      body: "Behind Schedule lists the deals whose FICA, deposit, bond-grant or balance dates have slipped -- the ones to chase, and the ones for the weekly management meeting. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "summary",
      route: "/behind-schedule",
      target: "behind-schedule-summary",
      section: "Count",
      title: "How many are behind",
      body: "This callout counts the deals currently behind schedule, so the size of the problem is clear at a glance.",
      placement: "bottom",
    },
    {
      id: "milestones",
      route: "/behind-schedule",
      target: "behind-schedule-milestones",
      section: "What has slipped",
      title: "What is behind",
      body: "Each tile counts the deals with one item slipped -- FICA, deposit, bond grant, the purchaser's other sale or balance -- and the worst days overdue. Click a tile to list only those deals; click it again to see them all.",
      placement: "bottom",
    },
    {
      id: "act",
      route: "/behind-schedule",
      target: "behind-schedule",
      section: "Act on it",
      title: "Chase, or extend",
      body: "Open any deal to see exactly which date has slipped -- FICA, deposit, bond grant or balance -- then chase it, or draw an extension document when a date genuinely needs to move. That is Behind Schedule.",
      placement: "center",
    },
  ],
};

/**
 * Agent Compliance roster (``/admin/compliance/agents`` + ``/crm/compliance/agents``)
 * -- the HR/office view of Natalie's agent-compliance spreadsheet: every agent's
 * contracts / FFC / PPRA registration / qualifications with a green/amber/red
 * status, a status summary, filters, and a per-agent detail drawer to record and
 * verify documents. This is the CRM-shell PAGE deep-dive, reachable from
 * Training; the admin route is wired to its admin-shell clone
 * (``admin-agent-compliance-page`` in adminPageTours.ts). The HR persona also has
 * its own welcome tour (``hr-welcome``) that lands on the same roster.
 *
 * Audience (#2452): gated on ``manage_compliance`` -- HR holds it and admins pass
 * any flag check, while a plain agent never reaches the route and must not be
 * offered it in Training. VALUE-FREE copy (HR never sees money). The record &
 * verify step is an action beacon gated on ``manage_compliance`` so a read-only
 * overseer (a manager via oversight) sees no write affordance.
 */
export const BACK_OFFICE_AGENT_COMPLIANCE_PAGE_TOUR: TourDefinition = {
  id: "back-office-agent-compliance-page",
  title: "Agent Compliance tour",
  audience: { flag: "manage_compliance" },
  steps: [
    {
      id: "welcome",
      route: "/crm/compliance/agents",
      target: "center",
      section: "Welcome",
      title: "Your compliance roster",
      body: "This roster tracks every agent's compliance -- contracts, FFC, PPRA registration and qualifications. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "roster",
      route: "/crm/compliance/agents",
      target: "page-admin-compliance-agents",
      section: "Roster",
      title: "Every agent, at a glance",
      body: "Each agent shows a green, amber or red status worked out from the recorded dates, so you can see who is in order and who needs attention. It replaces the spreadsheet.",
      placement: "bottom",
    },
    {
      id: "status-band",
      route: "/crm/compliance/agents",
      target: "compliance-summary",
      section: "Status summary",
      title: "The status summary",
      body: "These tiles count how many documents are valid, expiring soon, expired or missing across the agency, so the size of any gap is clear.",
      placement: "bottom",
    },
    {
      id: "find-agents",
      route: "/crm/compliance/agents",
      target: "compliance-roster",
      section: "Find an agent",
      title: "Search, filter and sort",
      body: "Search by name, filter by status, and sort by soonest expiry to pull up exactly the agents you need to work first.",
      placement: "bottom",
    },
    {
      // ACTION step (#1342): beacon on the roster/filters -- the stable anchor
      // present regardless of loading or empty state. Gated on manage_compliance
      // so a read-only overseer never sees the write affordance.
      id: "record-verify",
      route: "/crm/compliance/agents",
      target: "compliance-roster",
      kind: "action",
      section: "Record and verify",
      permFlag: "manage_compliance",
      title: "Record and verify a document",
      body: "Open an agent's row to record a document, set a reason code where something is outstanding, and verify it once it is in order. Give the beacon a click to see where to start.",
      placement: "bottom",
    },
    {
      id: "help",
      route: "/crm/compliance/agents",
      target: "center",
      section: "Help",
      title: "That is the roster",
      body: "Keep the roster up to date and every agent's compliance stays visible at a glance. That is the Agent Compliance roster -- explore at your own pace.",
      placement: "center",
    },
  ],
};

/**
 * Every per-page agent + back-office tour, in nav order. Spread into
 * TOUR_REGISTRY (registry.ts) and route-wired in routeTours.ts.
 */
export const AGENT_PAGE_TOURS: TourDefinition[] = [
  AGENT_FARM_PAGE_TOUR,
  AGENT_BUYERS_PAGE_TOUR,
  AGENT_APPOINTMENTS_PAGE_TOUR,
  AGENT_MESSAGING_PAGE_TOUR,
  AGENT_FICA_MATRIX_PAGE_TOUR,
  AGENT_REFERRALS_PAGE_TOUR,
  BACK_OFFICE_CONVEYANCERS_PAGE_TOUR,
  AGENT_CONNECT_PAGE_TOUR,
  AGENT_DEAL_DETAIL_PAGE_TOUR,
  BACK_OFFICE_FILE_COVER_PAGE_TOUR,
  BACK_OFFICE_BEHIND_SCHEDULE_TOUR,
  BACK_OFFICE_AGENT_COMPLIANCE_PAGE_TOUR,
];
